import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { toast } from 'sonner'
import { useStorefrontTenantSettings } from './useStorefrontTenantSettings'
import type { TenantSettingsResponse } from '@/lib/api/controlPlaneClient'
import { SettingRow, Divider } from './SettingRow'

export function StockManagementSettings() {
  const {
    settings,
    isLoading,
    isError,
    updateAsync,
    isUpdating,
    queryKey,
  } = useStorefrontTenantSettings()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (isError) {
      toast.error('Failed to load stock management settings')
    }
  }, [isError])

  const handleToggleStockReservation = async (checked: boolean) => {
    const previousData = queryClient.getQueryData<TenantSettingsResponse>(
      queryKey,
    )

    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) =>
        old
          ? { ...old, enable_stock_reservation: checked ? 1 : 0 }
          : old,
    )

    try {
      await updateAsync({ enable_stock_reservation: checked ? 1 : 0 })
      toast('Setting saved.')
    } catch {
      queryClient.setQueryData(queryKey, previousData)
      toast.error('Failed to save. Please try again.')
    }
  }

  return (
    <>
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1 mt-6 mb-2">
        Stock Management
      </p>
      <Card className="rounded-2xl border-0">
        <CardContent className="p-0">
          {/* Reserve Stock on Order */}
          <SettingRow label="Reserve stock on order placement">
            {isLoading ? (
              <Skeleton className="w-9 h-5 rounded-full" />
            ) : (
              <Switch
                checked={!!settings?.enable_stock_reservation}
                onCheckedChange={handleToggleStockReservation}
                disabled={isUpdating}
                aria-label="Reserve stock when order is placed"
              />
            )}
          </SettingRow>

          <Divider />

          {/* Stock Deduction Timing */}
          <SettingRow label="Stock deduction timing">
            {isLoading ? (
              <Skeleton className="w-32 h-4 rounded-md" />
            ) : (
              <span className="text-sm text-muted-foreground">
                {settings?.stock_deduction_timing || 'On Shipment'}
              </span>
            )}
          </SettingRow>
        </CardContent>
      </Card>
    </>
  )
}
